import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Gift, Search, Calendar, MapPin, ExternalLink, Bookmark, BookmarkCheck, Building, Filter, Users } from 'lucide-react';
import { formatDate } from '../lib/utils';

const TYPE_COLORS = {
  job_fair: 'bg-amber-50 text-amber-700',
  job_posting: 'bg-blue-50 text-blue-700',
  food_distribution: 'bg-emerald-50 text-emerald-700',
  clothing_drive: 'bg-purple-50 text-purple-700',
  housing: 'bg-sky-50 text-sky-700',
  training: 'bg-orange-50 text-orange-700',
  recovery_meeting: 'bg-teal-50 text-teal-700',
  transportation: 'bg-slate-50 text-slate-700',
  community_event: 'bg-rose-50 text-rose-700',
  scholarship: 'bg-indigo-50 text-indigo-700',
  volunteer: 'bg-pink-50 text-pink-700',
};

const TYPE_LABELS = {
  job_fair: 'Job Fair',
  job_posting: 'Job Posting',
  food_distribution: 'Food Distribution',
  clothing_drive: 'Clothing Drive',
  housing: 'Housing',
  training: 'Training Program',
  recovery_meeting: 'Recovery Meeting',
  transportation: 'Transportation',
  community_event: 'Community Event',
  scholarship: 'Scholarship',
  volunteer: 'Volunteer',
};

export default function OpportunitiesPage() {
  const { user, axiosAuth } = useAuth();
  const [opportunities, setOpportunities] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const api = axiosAuth();

  const load = async () => {
    try {
      const [oppsRes, savedRes] = await Promise.all([
        api.get('/opportunities'),
        api.get('/opportunities/saved/me').catch(() => ({ data: [] }))
      ]);
      setOpportunities(oppsRes.data || []);
      setSavedIds(new Set((savedRes.data || []).map(o => o.id)));
    } catch (e) {
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = opportunities.filter(o => {
    const matchesSearch = !search || 
      o.title.toLowerCase().includes(search.toLowerCase()) ||
      o.description?.toLowerCase().includes(search.toLowerCase()) ||
      o.organization_name?.toLowerCase().includes(search.toLowerCase());
    const matchesType = typeFilter === 'all' || o.opportunity_type === typeFilter;
    const isActive = o.status === 'active';
    const notExpired = !o.date || o.date === '' || new Date(o.date) >= new Date(new Date().toDateString());
    return matchesSearch && matchesType && isActive && notExpired;
  });

  const handleSave = async (oppId) => {
    try {
      const res = await api.post(`/opportunities/${oppId}/interest`);
      if (res.data.interested) {
        setSavedIds(prev => new Set([...prev, oppId]));
        toast.success('Opportunity saved!');
      } else {
        setSavedIds(prev => {
          const next = new Set(prev);
          next.delete(oppId);
          return next;
        });
        toast.success('Removed from saved');
      }
    } catch (e) {
      toast.error('Failed to save opportunity');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="opportunities-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Community Opportunities</h1>
        <p className="text-muted-foreground text-sm mt-1">Discover resources, events, and opportunities in your community</p>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input 
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search opportunities..."
            className="pl-9 rounded-full"
            data-testid="opp-search"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-48 rounded-full" data-testid="opp-type-filter">
            <Filter size={14} className="mr-2" />
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {Object.entries(TYPE_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Opportunities Grid */}
      {filtered.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(opp => {
            const isSaved = savedIds.has(opp.id);
            return (
              <Card 
                key={opp.id} 
                className="border-0 shadow-sm rounded-2xl hover:shadow-md transition-all cursor-pointer group"
                onClick={() => setSelected(opp)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${TYPE_COLORS[opp.opportunity_type]?.split(' ')[0] || 'bg-slate-50'}`}>
                      <Gift className={`w-5 h-5 ${TYPE_COLORS[opp.opportunity_type]?.split(' ')[1] || 'text-slate-600'}`} />
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 rounded-full"
                      onClick={(e) => { e.stopPropagation(); handleSave(opp.id); }}
                      data-testid={`save-opp-${opp.id}`}
                    >
                      {isSaved ? (
                        <BookmarkCheck className="w-4 h-4 text-teal-600" />
                      ) : (
                        <Bookmark className="w-4 h-4 text-muted-foreground group-hover:text-teal-600" />
                      )}
                    </Button>
                  </div>
                  <Badge className={`mb-2 ${TYPE_COLORS[opp.opportunity_type] || 'bg-slate-100 text-slate-600'}`}>
                    {TYPE_LABELS[opp.opportunity_type] || opp.opportunity_type}
                  </Badge>
                  <h3 className="font-semibold text-sm line-clamp-2">{opp.title}</h3>
                  {opp.organization_name && (
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                      <Building size={10} /> {opp.organization_name}
                    </p>
                  )}
                  {opp.description && (
                    <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{opp.description}</p>
                  )}
                  <div className="flex flex-wrap gap-3 mt-3 text-xs text-muted-foreground">
                    {(opp.date || opp.is_recurring) && (
                      <span className="flex items-center gap-1">
                        <Calendar size={10} /> 
                        {opp.is_recurring ? opp.recurrence_details || 'Recurring' : opp.date}
                      </span>
                    )}
                    {opp.location && (
                      <span className="flex items-center gap-1">
                        <MapPin size={10} /> {opp.location}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border-2 border-dashed rounded-2xl">
          <CardContent className="py-12 text-center">
            <Gift className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">No opportunities found matching your criteria.</p>
          </CardContent>
        </Card>
      )}

      {/* Detail Modal */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="rounded-2xl max-w-lg">
          {selected && (
            <>
              <DialogHeader>
                <div className="flex items-start gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${TYPE_COLORS[selected.opportunity_type]?.split(' ')[0] || 'bg-slate-50'}`}>
                    <Gift className={`w-6 h-6 ${TYPE_COLORS[selected.opportunity_type]?.split(' ')[1] || 'text-slate-600'}`} />
                  </div>
                  <div>
                    <DialogTitle className="text-left">{selected.title}</DialogTitle>
                    <Badge className={`mt-1 ${TYPE_COLORS[selected.opportunity_type] || 'bg-slate-100'}`}>
                      {TYPE_LABELS[selected.opportunity_type]}
                    </Badge>
                  </div>
                </div>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                {selected.organization_name && (
                  <div className="flex items-center gap-2 text-sm">
                    <Building size={14} className="text-muted-foreground" />
                    <span>{selected.organization_name}</span>
                  </div>
                )}
                {selected.description && (
                  <p className="text-sm text-muted-foreground">{selected.description}</p>
                )}
                <div className="space-y-2 text-sm">
                  {(selected.date || selected.is_recurring) && (
                    <div className="flex items-center gap-2">
                      <Calendar size={14} className="text-muted-foreground" />
                      <span>
                        {selected.is_recurring 
                          ? `Recurring: ${selected.recurrence_details || 'See details'}` 
                          : `${selected.date} ${selected.time ? `at ${selected.time}` : ''}`}
                      </span>
                    </div>
                  )}
                  {selected.location && (
                    <div className="flex items-center gap-2">
                      <MapPin size={14} className="text-muted-foreground" />
                      <span>{selected.location}</span>
                    </div>
                  )}
                  {selected.eligibility && (
                    <div className="flex items-start gap-2">
                      <Users size={14} className="text-muted-foreground mt-0.5" />
                      <span>Eligibility: {selected.eligibility}</span>
                    </div>
                  )}
                  {selected.capacity && (
                    <div className="text-xs text-muted-foreground">
                      Capacity: {selected.capacity} spots
                    </div>
                  )}
                </div>
                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    className="flex-1 rounded-full"
                    onClick={() => handleSave(selected.id)}
                  >
                    {savedIds.has(selected.id) ? (
                      <><BookmarkCheck size={14} className="mr-2" /> Saved</>
                    ) : (
                      <><Bookmark size={14} className="mr-2" /> Save</>
                    )}
                  </Button>
                  {selected.application_link && (
                    <Button 
                      className="flex-1 bg-teal-600 hover:bg-teal-700 text-white rounded-full"
                      onClick={() => window.open(selected.application_link, '_blank')}
                    >
                      <ExternalLink size={14} className="mr-2" /> Apply
                    </Button>
                  )}
                </div>
                {selected.contact_info && (
                  <p className="text-xs text-muted-foreground text-center">
                    Contact: {selected.contact_info}
                  </p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
